import { create } from "zustand";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { getErrorMessage, isAbortError } from "@/lib/errors";
import type { Site, SiteUpdateBody } from "@/features/sites/types/site";
import { normalizeHalfhoursMatrix } from "@/features/sites/utils/siteAvailabilityUtils";

type SitesState = {
  sites: Record<number, Site>;
  loading: boolean;
  error?: string;
};

type SitesActions = {
  list: (signal?: AbortSignal) => Promise<void>;
  get: (id: number, signal?: AbortSignal) => Promise<Site | undefined>;
  create: (payload: Partial<Site>) => Promise<Site | undefined>;
  update: (id: number, payload: SiteUpdateBody) => Promise<void>;
  remove: (id: number) => Promise<boolean>;
};


function normalizeSite(site: Site): Site {
  return {
    ...site,
    specialties: site.specialties ?? [],
    offboarded_on: site.offboarded_on ?? null,
    halfhours_active: normalizeHalfhoursMatrix(site.halfhours_active),
  };
}

export const useSitesStore = create<SitesState & SitesActions>((set, get) => ({
  sites: {},
  loading: false,

  async list(signal) {
    set({ loading: true, error: undefined });
    try {
      const data = await http<Site[]>("/api/v1/sites", {}, signal);
      const normalized = data.map(normalizeSite);
      set({ sites: normalized.reduce((acc, s) => ({ ...acc, [s.id]: s }), {}), loading: false });
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      set({ loading: false, error: message });
      toast.error("Failed to load sites", { description: message });
    }
  },

  async get(id, signal) {
    try {
      const data = await http<Site>(`/api/v1/sites/${id}`, {}, signal);
      const normalized = normalizeSite(data);
      set({ sites: { ...get().sites, [normalized.id]: normalized } });
      return normalized;
    } catch (err: unknown) {
      if (isAbortError(err)) return;
      toast.error("Failed to get site", { description: getErrorMessage(err) });
    }
  },

  async create(payload) {
    try {
      const data = await http<Site>(`/api/v1/sites`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const normalized = normalizeSite(data);
      set({ sites: { ...get().sites, [normalized.id]: normalized } });
      toast.success("Site created");
      await get().list();
      return normalized;
    } catch (err: unknown) {
      toast.error("Failed to create site", { description: getErrorMessage(err) });
      return undefined;
    }
  },

  async update(id, payload) {
    try {
      const data = await http<Site>(`/api/v1/sites/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      const normalized = normalizeSite(data);
      set({ sites: { ...get().sites, [normalized.id]: normalized } });
      toast.success("Site updated");
    } catch (err: unknown) {
      toast.error("Failed to update site", { description: getErrorMessage(err) });
    }
  },

  async remove(id) {
    try {
      await http<void>(`/api/v1/sites/${id}`, { method: "DELETE" });
      toast.success("Site removed");
      set({ sites: Object.fromEntries(Object.entries(get().sites).filter(([key]) => key !== String(id))) });
      return true;
    } catch (err: unknown) {
      toast.error("Failed to remove site", { description: getErrorMessage(err) });
      return false;
    }
  },
}));
